import { Storage } from "@mui/icons-material";
import { Chip, CircularProgress, List, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import qs from "query-string";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AccountabilityContext from "../../../../../context/AccountabilityLogContext";
import DataContext from "../../../../../context/DataContext";
import { useDebouncedEffect } from "../../../../../hooks/useDebounceEffect";
import axios from "../../../../api/axios";
import { CustomChip } from "../../../../global/CustomQueryChip";
import DateRange from "../../../../global/DateRange";
import { createDisplayObj, filterFieldData } from "../../../../global/helpers";
import MultiSelectFilterV2 from "../../../../global/MultiSelectFilterV2";
import MultiSelectSearchV2 from "../../../../global/MultiSelectSearchV2";
import OrderingArrow from "../../../../global/OrderingArrow";
import PaginationControls from "../../../../global/PaginationControls";

import LogItem from "./components/LogItem";

export default function AccountabilityLog() {
  const { accessToken, userRoles, loggedInUser } = useContext(DataContext);
  const {
    search,
    setSearch,
    paginationUrl,
    setPaginationUrl,
    currentPage,
    setCurrentPage,
    resultCount,
    setResultCount,
    orderingVal,
    setOrderingVal,
    timeStampTo,
    setTimeStampTo,
    timeStampFrom,
    setTimeStampFrom,
    selectedActions,
    setSelectedActions,
    activeActions,
    setActiveActions,
    activeFilters,
    setActiveFilters,
    selectedUsers,
    setSelectedUsers,
    activeUsers,
    setActiveUsers,
    selectedActionLabels,
    setSelectedActionLabels,
    activeActionLabels,
    setActiveActionLabels,
  } = useContext(AccountabilityContext);
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [actionGroupOptions, setActionGroupOptions] = useState([]);
  const [actionGroupLabels, setActionGroupLabels] = useState("");
  const [actionLabelOptions, setActionLabelOptions] = useState([]);
  const [actionLabels, setActionLabels] = useState("");
  const [crudLabels, setCrudLabels] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedUsers.length) {
      setSelectedUsers([loggedInUser.pk]);
      setActiveUsers([loggedInUser.name]);
    } else if (selectedUsers.length > 1 && selectedUsers.includes(loggedInUser.pk)) {
      setSelectedUsers(selectedUsers.filter((item) => item !== loggedInUser.pk));
      setActiveUsers(activeUsers.filter((item) => item !== loggedInUser.name));
    }
  }, [setSelectedUsers, loggedInUser, setActiveUsers, activeUsers, selectedUsers]);

  useEffect(() => {
    axios
      .get(`/api/field_options/?ordering=label&content_type=actionlog&limit=150`, {
      })
      .then((response) => {
        const data = response.data.results;
        //*Action Group - Labels
        setActionGroupLabels(createDisplayObj(data, "action_group"));
        setActionGroupOptions(filterFieldData(data, "action_group"));
        //*Crud Labels
        setCrudLabels(createDisplayObj(data, "crud_type"));
        //*Action Label - Labels
        setActionLabels(createDisplayObj(data, "action_label"));
        setActionLabelOptions(filterFieldData(data, "action_label"));
        return response;
      })
      .catch((response) => {
        return response;
      });
  }, [
    setActionGroupLabels,
    setActionGroupOptions,
    accessToken,
    setCrudLabels,
    setActionLabels,
    setActionLabelOptions,
  ]);

  const handleChange = (event, value) => {
    setCurrentPage(value);
    if (value === 1) {
      setPaginationUrl(`?limit=25&`);
      return;
    }
    setPaginationUrl(`limit=25&offset=${25 * (value - 1)}&`);
  };

  const resetPagination = () => {
    setPaginationUrl("");
    setCurrentPage(1);
  };

  const queryActionLogs = () => {
    if (userRoles.permissions.includes("utilities.view_actionlog")) {
      setLoading(true);

      const query = qs.stringify(
        {
          search: search === "" ? null : search,
          timestamp__gte: !timeStampFrom ? null : format(new Date(timeStampFrom), "yyyy-MM-dd"),
          timestamp__lte: !timeStampTo ? null : format(new Date(timeStampTo), "yyyy-MM-dd"),
          ordering: !orderingVal ? null : orderingVal,
        },
        {
          skipNull: true,
        }
      );

      const usersQuery = selectedUsers.length === 0 ? "" : `&user__in=${selectedUsers}`;

      const actionQuery =
        selectedActions.length === 0 ? "" : `&action_group__in=${selectedActions}`;

      const actionLabelQuery =
        selectedActionLabels.length === 0 ? "" : `&action_label__in=${selectedActionLabels}`;

      axios
        .get(`/api/logs/?` + paginationUrl + query + actionQuery + actionLabelQuery + usersQuery, {
        })
        .then((response) => {
          const data = response.data.results;
          setResultCount({
            total: response.data.count,
            current: response.data.results.length,
          });
          setResponseData(data);
          setLoading(false);
          return response;
        })
        .catch((error) => {
          setLoading(false);
          setErrorMessage("There was an error loading this content");
          if (error.response.status === 403) {
            navigate("/dashboard");
          }
          return error;
        });
      const parsedQuery = qs.parse(query);
      setActiveFilters(parsedQuery);
    } else {
      setErrorMessage("You do not have permission to access this content");
    }
  };

  const displayFilters = (object) => {
    const returnArr = [];
    for (const [key, value] of Object.entries(object)) {
      if (key === "timestamp__gte") {
        returnArr.push(`Date After: ${value}`);
      } else if (key === "timestamp__lte") {
        returnArr.push(`Date Before: ${value}`);
      } else {
        returnArr.push(`${key}: ${value}`);
      }
    }
    return returnArr;
  };

  useDebouncedEffect(
    () => {
      queryActionLogs();
    },
    [
      search,
      paginationUrl,
      orderingVal,
      timeStampFrom,
      timeStampTo,
      selectedActions,
      selectedActionLabels,
      selectedUsers,
    ],
    500
  );

  const clearFilter = (val) => {
    const key = val.split(":")[0];
    if (key === "Date After") {
      setTimeStampFrom();
    } else if (key === "Date Before") {
      setTimeStampTo();
    } else if (key === "search") {
      setSearch("");
    } else if (key === "ordering") {
      setOrderingVal("");
    }
  };

  const tableHeaderClasses = "text-center font-bold";

  return userRoles.permissions.includes("utilities.view_actionlog") ? (
    <div className="relative bg-white p-2">
      <div className="flex flex-col">
        <div className="relative w-full">
          <h1 className="my-4 w-1/4 text-[1.2rem]">
            <Storage /> Action Log
          </h1>
        </div>
        <div className="relative mb-2 flex w-full items-center py-6">
          <div className="absolute bottom-0 top-0 z-10 w-[23%] rounded-md bg-white py-2 shadow-md">
            {userRoles.permissions.includes("users.view_usertomanagerassignments") ? (
              <MultiSelectSearchV2
                title="Filter by Managed User"
                searchTitle="User Search"
                selectedItems={selectedUsers}
                setSelectedItems={setSelectedUsers}
                setActiveItems={setActiveUsers}
                activeItems={activeUsers}
                resetPagination={resetPagination}
                nameKey={"name"}
                valueKey={"pk"}
                apiUrl={`/api/users/managed_users/?`}
                buttonFullW={true}
                dropdown={true}
              />
            ) : (
              <p>Filter unavailable</p>
            )}
          </div>
          <div className="absolute bottom-0 left-1/4 top-0 z-10 w-[23%] rounded-md bg-white py-2 shadow-md">
            <div>
              <MultiSelectFilterV2
                title="Filter by Group"
                responseArray={actionGroupOptions}
                selectedItems={selectedActions}
                setSelectedItems={setSelectedActions}
                setActiveItems={setActiveActions}
                activeItems={activeActions}
                nameKey="label"
                valueKey="key"
                resetPagination={resetPagination}
                searchTitle="Actions"
                buttonFullW={true}
                dropdown={true}
              />
            </div>
          </div>
          <div className="absolute bottom-0 left-1/2 top-0 z-10 w-[23%] rounded-md bg-white py-2 shadow-md">
            <MultiSelectFilterV2
              title="Filter by Label"
              responseArray={actionLabelOptions}
              selectedItems={selectedActionLabels}
              setSelectedItems={setSelectedActionLabels}
              setActiveItems={setActiveActionLabels}
              activeItems={activeActionLabels}
              nameKey="label"
              valueKey="key"
              resetPagination={resetPagination}
              searchTitle="Actions"
              buttonFullW={true}
              dropdown={true}
            />
          </div>
          <div className="absolute bottom-0 left-3/4 top-0 z-10 w-[23%] rounded-md bg-white px-0 py-0 shadow-md">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateRange
                val="Filter by Date"
                dateFrom={timeStampFrom}
                dateTo={timeStampTo}
                setDateFrom={setTimeStampFrom}
                setDateTo={setTimeStampTo}
                resetPagination={resetPagination}
                className="absolute m-0 bg-white p-0 shadow-none"
                dropdown={true}
              />
            </LocalizationProvider>
          </div>
        </div>
      </div>
      <div className="relative mx-4 flex justify-between">
        <div className="w-3/4">
          {activeFilters
            ? displayFilters(activeFilters).map((filt, idx) => (
                <Chip
                  className={`m-1 rounded-lg px-2 text-center shadow-inner`}
                  key={filt + idx}
                  onDelete={() => clearFilter(filt)}
                  label={filt}
                />
              ))
            : ""}
          <CustomChip
            setActiveItems={setActiveActions}
            activeItems={activeActions}
            selectedItems={selectedActions}
            setSelectedItems={setSelectedActions}
            title="Groups"
            resetPagination={resetPagination}
          />
          <CustomChip
            setActiveItems={setActiveActionLabels}
            activeItems={activeActionLabels}
            selectedItems={selectedActionLabels}
            setSelectedItems={setSelectedActionLabels}
            title="Labels"
            resetPagination={resetPagination}
          />
          <CustomChip
            setActiveItems={setActiveUsers}
            activeItems={activeUsers}
            selectedItems={selectedUsers}
            setSelectedItems={setSelectedUsers}
            title="Users"
            resetPagination={resetPagination}
          />
        </div>
        <p className="self-end py-4 pl-5">
          Showing: {resultCount.current} / {resultCount.total}
        </p>
      </div>
      <div className="flex">
        <TextField
          className="my-2 w-full text-center"
          id="action-log-search"
          label="Search logs"
          value={search}
          size="small"
          onChange={(event) => {
            setSearch(event.target.value);
            resetPagination();
          }}
        />
      </div>
      <div className="flex justify-between px-6">
        <div className={`w-[5%] ${tableHeaderClasses}`}>Action</div>
        <div className={`w-[15%] ${tableHeaderClasses}`}>
          User{" "}
          <OrderingArrow
            val="user__name"
            orderingVal={orderingVal}
            setOrderingVal={setOrderingVal}
          />
        </div>
        <div className={`w-[15%] ${tableHeaderClasses}`}>
          Client{" "}
          <OrderingArrow
            val="related_case__name"
            orderingVal={orderingVal}
            setOrderingVal={setOrderingVal}
          />
        </div>
        <div className={`w-[15%] ${tableHeaderClasses}`}>
          Date
          <OrderingArrow
            val="timestamp"
            orderingVal={orderingVal}
            setOrderingVal={setOrderingVal}
          />
        </div>
        <div className={`w-[15%] ${tableHeaderClasses}`}>Group</div>
        <div className={`w-[15%] ${tableHeaderClasses}`}>Label</div>
        <div className={`w-[5%] ${tableHeaderClasses}`}>&nbsp;</div>
      </div>
      <List className="h-[50vh] overflow-y-auto">
        {errorMessage ? (
          <p className="text-center">{errorMessage}</p>
        ) : loading ? (
          <div className="mx-auto w-fit">
            <CircularProgress color="secondary" />
          </div>
        ) : responseData.length === 0 || !responseData ? (
          <p className="text-center">No results found</p>
        ) : (
          responseData.map((log) => (
            <LogItem
              log={log}
              key={log.id}
              actionGroupLabels={actionGroupLabels}
              actionLabels={actionLabels}
              crudLabels={crudLabels}
            />
          ))
        )}
      </List>
      <div className="mx-auto my-4 w-fit">
        <PaginationControls
          resultCount={resultCount}
          handleChange={handleChange}
          currentPage={currentPage}
        />
      </div>
    </div>
  ) : (
    <p className="bg-white p-2 text-center">{errorMessage}</p>
  );
}
